import type { CMSAssetDashboardVerification, CMSDashboardStatsTableColumn } from '../../types';
import type { TableProps } from '@workramp/ui';
import { Icon, Table } from '@workramp/ui';
import { useTheme } from 'styled-components';
import { useNavigate } from 'react-router';
import { APP_CONSTANTS, I18n } from '~/common/explicit-exports';
import { DateCell } from '~/features/tables';
import { CMSAssetVerifyButton } from '../CMSAssetVerifyButton';
import { useGetDashboardVerification } from '../../api';
import { StyledButton } from '../CMSDashbboardActivityTable/CMSDashboardActivityTable.styles';
import { getCMSAssetLink } from '../../utils';
import { CMSDashboardVerificationEmptyState } from './CMSDashboardVerificationEmptyState';

enum ColumnKeys {
  lastVerifiedDate = 'lastVerifiedDate',
  title = 'title',
  expiration = 'expiration',
  reviewer = 'reviewer',
  daysUnverified = 'daysUnverified',
  action = 'action',
}

const { t } = I18n;

const TRANSLATED_TITLE = t('js.admin.cms.dashboard_table.columns.title');
const TRANSLATED_LAST_VERIFIED = t('js.admin.cms.dashboard_table.columns.last_verified');
const TRANSLATED_EXPIRATION = t('js.admin.cms.dashboard_table.columns.expiration');
const TRANSLATED_REVIEWER = t('js.admin.cms.dashboard_table.columns.reviewer');
const TRANSLATED_DAYS_UNVERIFIED = t('js.admin.cms.dashboard_table.columns.days_unverified');
const TRANSLATED_REVIEWER_ALL_CMS_ADMINS = t('js.admin.cms.dashboard_table.columns.all_cms_admins');

export const columns: CMSDashboardStatsTableColumn[] = [
  { name: TRANSLATED_TITLE, key: ColumnKeys.title, allowSorting: false },
  { name: TRANSLATED_LAST_VERIFIED, key: ColumnKeys.lastVerifiedDate, allowSorting: false },
  { name: TRANSLATED_EXPIRATION, key: ColumnKeys.expiration, allowSorting: false },
  { name: TRANSLATED_REVIEWER, key: ColumnKeys.reviewer, allowSorting: false },
  { name: TRANSLATED_DAYS_UNVERIFIED, key: ColumnKeys.daysUnverified, allowSorting: false },
  { name: '', key: ColumnKeys.action, allowSorting: false },
];

export const CMSDashboardVerificationTable = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const getDashboardVerification = useGetDashboardVerification();

  const tableProps: TableProps<never> = {
    isLoading: getDashboardVerification.isLoading,
    showLastRowDivider: true,
    rowPadding: theme.spacing(8),
    emptyState: <CMSDashboardVerificationEmptyState />,
  };

  return (
    <Table {...tableProps}>
      <Table.Header>
        {columns.map((column) => (
          <Table.Column key={column.key} allowsSorting={column.allowSorting}>
            {column.name}
          </Table.Column>
        ))}
      </Table.Header>
      <Table.Body>
        {getDashboardVerification.data?.map(
          ({
            assetId,
            assetTitle,
            lastVerifiedAt,
            expirationAt,
            reviewer,
            daysUnverified,
            assetType,
            isVerifiableByCmsAdmins,
          }: CMSAssetDashboardVerification) => (
            <Table.Row key={assetId}>
              <Table.Cell>
                <StyledButton
                  aria-label={`button--${assetTitle}`}
                  leadingIcon={
                    assetType === APP_CONSTANTS.CMS_ASSET_TYPES.LINK ? (
                      <Icon name="link:regular" color={theme.colors.decorative.neutral[900]} />
                    ) : (
                      <Icon name="file-document:regular" color={theme.colors.decorative.neutral[900]} />
                    )
                  }
                  onPress={() => navigate(getCMSAssetLink(assetType, assetId))}
                  variant="text"
                  tooltip={assetTitle}
                >
                  {assetTitle}
                </StyledButton>
              </Table.Cell>
              <Table.Cell>
                <DateCell showTime isLocalTime date={lastVerifiedAt} />
              </Table.Cell>
              <Table.Cell>
                <DateCell date={expirationAt} />
              </Table.Cell>
              <Table.Cell>{isVerifiableByCmsAdmins ? TRANSLATED_REVIEWER_ALL_CMS_ADMINS : reviewer.name}</Table.Cell>
              <Table.Cell>{daysUnverified === -1 ? '--' : daysUnverified}</Table.Cell>
              <Table.Cell>
                <CMSAssetVerifyButton cmsAssetId={assetId} />
              </Table.Cell>
            </Table.Row>
          ),
        )}
      </Table.Body>
    </Table>
  );
};
